import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  MoreVertical,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/shared/EmptyState';
import { AddReviewerDialog } from './AddReviewerDialog';
import { EditReviewerDialog } from './EditReviewerDialog';
import { api } from '@/lib/api';
import type { ReviewerRecord } from '@/types/api';
import { cn } from '@/lib/utils';

interface ReviewersTabProps {
  readonly openAddDialog: boolean;
  readonly onAddDialogClose: () => void;
}

const ROLE_LABEL: Record<ReviewerRecord['role'], string> = {
  cco: 'CCO',
  compliance_rep: 'Compliance Rep',
  department_rep: 'Department Rep',
};

const ROLE_BADGE_STYLE: Record<ReviewerRecord['role'], string> = {
  cco: 'bg-teal/15 text-teal-light',
  compliance_rep: 'bg-blue-500/15 text-blue-400',
  department_rep: 'bg-amber-500/15 text-amber-400',
};

export function ReviewersTab({ openAddDialog, onAddDialogClose }: ReviewersTabProps) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ReviewerRecord | null>(null);

  useEffect(() => {
    if (openAddDialog) {
      setAddOpen(true);
      onAddDialogClose();
    }
  }, [openAddDialog, onAddDialogClose]);

  const { data: reviewers, isLoading } = useQuery({
    queryKey: ['reviewers'],
    queryFn: () => api.admin.reviewers.list(),
    staleTime: 60_000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.admin.reviewers.toggleActive(id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? 'Reviewer activated.' : 'Reviewer deactivated.');
      queryClient.invalidateQueries({ queryKey: ['reviewers'] });
    },
    onError: () => toast.error('Failed to update reviewer status.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.admin.reviewers.delete(id),
    onSuccess: () => {
      toast.success('Reviewer removed.');
      queryClient.invalidateQueries({ queryKey: ['reviewers'] });
    },
    onError: () => toast.error('Failed to remove reviewer.'),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 pt-6">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          {reviewers?.length ?? 0} Reviewer{reviewers?.length === 1 ? '' : 's'}
        </h3>
        <Button
          className="bg-teal text-white hover:bg-teal/90"
          size="sm"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add Reviewer
        </Button>
      </div>

      {reviewers && reviewers.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border">
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-border bg-muted/30 px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <div className="w-48">Name</div>
            <div className="w-48">Email</div>
            <div className="w-32">Role</div>
            <div className="flex-1">Departments</div>
            <div className="w-20 text-center">Status</div>
            <div className="w-10" />
          </div>

          {/* Rows */}
          {reviewers.map((reviewer) => (
            <div
              key={reviewer.reviewerId}
              className={cn(
                'flex items-center gap-4 border-b border-border px-4 py-3 last:border-b-0 transition-colors',
                !reviewer.isActive && 'opacity-50',
              )}
            >
              <div className="w-48 min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {reviewer.name}
                </p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {reviewer.reviewerId.slice(0, 8)}
                </p>
              </div>

              <div className="w-48 min-w-0">
                <p className="truncate text-sm text-muted-foreground">{reviewer.email}</p>
              </div>

              <div className="w-32">
                <span
                  className={cn(
                    'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
                    ROLE_BADGE_STYLE[reviewer.role],
                  )}
                >
                  {ROLE_LABEL[reviewer.role]}
                </span>
              </div>

              <div className="flex flex-1 flex-wrap gap-1 min-w-0">
                {reviewer.departments.map((dept) => (
                  <span
                    key={dept}
                    className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground"
                  >
                    {dept.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>

              <div className="w-20 text-center">
                <span
                  className={cn(
                    'text-xs font-semibold',
                    reviewer.isActive ? 'text-green-400' : 'text-red-400',
                  )}
                >
                  {reviewer.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="w-10">
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditTarget(reviewer)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        toggleMutation.mutate({
                          id: reviewer.reviewerId,
                          isActive: !reviewer.isActive,
                        })
                      }
                    >
                      {reviewer.isActive ? (
                        <>
                          <ToggleLeft className="mr-2 h-3.5 w-3.5" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <ToggleRight className="mr-2 h-3.5 w-3.5" />
                          Activate
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-400 focus:text-red-400"
                      onClick={() => {
                        if (window.confirm(`Remove ${reviewer.name}?`)) {
                          deleteMutation.mutate(reviewer.reviewerId);
                        }
                      }}
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Users}
          title="No reviewers yet"
          description="Add reviewers to your organization to enable the compliance review workflow."
          action={
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              onClick={() => setAddOpen(true)}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Your First Reviewer
            </Button>
          }
        />
      )}

      <AddReviewerDialog open={addOpen} onOpenChange={setAddOpen} />

      {editTarget && (
        <EditReviewerDialog
          open={!!editTarget}
          onOpenChange={(v) => { if (!v) setEditTarget(null); }}
          reviewer={editTarget}
        />
      )}
    </div>
  );
}
